import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, LinearProgress, Tooltip, Typography } from '@mui/material';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { usePermissions } from '../../../utils/usePermissions';
import { notify } from '../../notifications';
import { EditEmptyState } from '../reports/edit/EditCommon';
import { downloadBlob } from '../Documents/format';
import {
  AuditFilterOptions, AuditFilters, AuditListResponse, auditErrorMessage, fetchAuditCsv, fetchAuditFilterOptions, fetchAuditList
} from './auditApi';
import { csvFileName, defaultFilters, isRangeValid } from './auditFormat';
import AuditFiltersBar from './AuditFiltersBar';
import AuditTable, { PAGE_SIZE_OPTIONS } from './AuditTable';
import AuditDetailsDrawer from './AuditDetailsDrawer';
import AuditCleanupDialog from './AuditCleanupDialog';

/** Пауза после последнего изменения фильтров: текстовые поля порождают запрос на каждую набранную букву */
const FETCH_DEBOUNCE_MS = 300;
const BUTTON_SX = { textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none' } as const;

const AuditLog: React.FC = () => {
  const { hasPerm } = usePermissions();
  const canDetails = hasPerm('audit.details.view');
  const canExport = hasPerm('audit.export');
  const canCleanup = hasPerm('audit.cleanup');

  const [filters, setFilters] = useState<AuditFilters>(defaultFilters);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[1]);
  const [options, setOptions] = useState<AuditFilterOptions | null>(null);

  const [data, setData] = useState<AuditListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);

  useEffect(() => {
    document.title = 'Журнал действий | Zerde';
  }, []);

  // Справочники фильтров нужны только для выпадающих списков: без них журнал остается рабочим
  useEffect(() => {
    let cancelled = false;
    fetchAuditFilterOptions()
      .then((res) => {
        if (!cancelled) setOptions(res);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const rangeOk = isRangeValid(filters.from, filters.to);

  useEffect(() => {
    if (!rangeOk) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    const timer = setTimeout(() => {
      fetchAuditList(filters, page + 1, pageSize)
        .then((res) => {
          if (cancelled) return;
          // После очистки журнала страница могла опустеть: возвращаемся на последнюю
          if (res.rows.length === 0 && res.total > 0 && page > 0) {
            setPage(Math.max(0, res.pages - 1));
            return;
          }
          setData(res);
          setLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          setData(null);
          setError(auditErrorMessage(err, 'Не удалось загрузить журнал'));
          setLoading(false);
        });
    }, FETCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [filters, page, pageSize, rangeOk, reloadKey]);

  const handleFiltersChange = useCallback((patch: Partial<AuditFilters>) => {
    setPage(0);
    setFilters((prev) => {
      const next = { ...prev, ...patch };
      // Действие принадлежит модулю: при смене модуля прежний выбор действия сбрасывается
      if (patch.module !== undefined && patch.module !== prev.module) next.action = '';
      return next;
    });
  }, []);

  const handleReset = useCallback(() => {
    setPage(0);
    setFilters(defaultFilters());
  }, []);

  const handlePageSizeChange = useCallback((next: number) => {
    setPage(0);
    setPageSize(next);
  }, []);

  const handleRefresh = () => setReloadKey((key) => key + 1);

  const handleOpen = useCallback((row: { id: number }) => setSelectedId(row.id), []);
  const handleCloseDetails = useCallback(() => setSelectedId(null), []);

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const blob = await fetchAuditCsv(filters);
      downloadBlob(csvFileName(filters), blob);
    } catch (err) {
      notify.error(auditErrorMessage(err, 'Не удалось выгрузить журнал'));
    } finally {
      setExporting(false);
    }
  };

  const renderBody = () => {
    if (!rangeOk) {
      const reversed = filters.from !== '' && filters.to !== '' && filters.from > filters.to;
      return (
        <EditEmptyState
          icon={<EventBusyIcon />}
          title="Проверьте период"
          text={reversed ? 'Начальная дата позже конечной.' : 'Укажите корректные даты периода.'}
        />
      );
    }
    if (error) {
      return (
        <EditEmptyState
          icon={<ErrorOutlineIcon />}
          title="Не удалось загрузить журнал"
          text={error}
          action={<Button onClick={handleRefresh} sx={{ textTransform: 'none', fontWeight: 600 }}>Повторить</Button>}
        />
      );
    }
    if (!data) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }
    if (data.total === 0) {
      return (
        <EditEmptyState
          icon={<SearchOffIcon />}
          title="Записей не найдено"
          text="За выбранный период нет записей, подходящих под условия. Измените фильтры или расширьте период."
        />
      );
    }
    return (
      <AuditTable
        rows={data.rows}
        total={data.total}
        page={page}
        pageSize={pageSize}
        selectedId={selectedId}
        clickable={canDetails}
        stale={loading}
        onOpen={handleOpen}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
      />
    );
  };

  return (
    <Box>
      <AuditFiltersBar filters={filters} options={options} onChange={handleFiltersChange} onReset={handleReset} />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1, minHeight: 36 }}>
        <Typography sx={{ fontSize: '0.84rem', color: '#64748b' }}>
          {data && rangeOk && !error
            ? `Записей: ${data.total}${options && options.retentionDays ? ` · хранятся ${Math.round(options.retentionDays / 365)} г.` : ''}`
            : ''}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Обновить" arrow>
          <span>
            <IconButton size="small" onClick={handleRefresh} disabled={loading || !rangeOk} sx={{ color: '#64748b' }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {canExport && (
          <Button
            variant="outlined"
            onClick={handleExport}
            disabled={exporting || loading || !rangeOk || !data || data.total === 0}
            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
            sx={{ ...BUTTON_SX, color: '#334155', borderColor: '#cbd5e1' }}
          >
            Выгрузить CSV
          </Button>
        )}
        {canCleanup && (
          <Button
            variant="outlined"
            onClick={() => setCleanupOpen(true)}
            startIcon={<DeleteSweepOutlinedIcon />}
            sx={{ ...BUTTON_SX, color: '#b91c1c', borderColor: '#fecaca' }}
          >
            Очистка
          </Button>
        )}
      </Box>

      <Box sx={{ height: 2, mb: 1, mx: 0.5 }}>
        {loading && rangeOk && <LinearProgress sx={{ height: 2, borderRadius: 1 }} />}
      </Box>

      {renderBody()}

      <AuditDetailsDrawer recordId={canDetails ? selectedId : null} onClose={handleCloseDetails} onOpenRelated={setSelectedId} />
      {canCleanup && <AuditCleanupDialog open={cleanupOpen} onClose={() => setCleanupOpen(false)} onCleaned={handleRefresh} />}
    </Box>
  );
};

export default AuditLog;
